/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// Utils
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';
import { useMediaQuery } from 'react-responsive';

// Icons
import { ReactComponent as EyeIcon } from '../../../assets/icons/view_password.svg';
import { ReactComponent as UpIcon } from '../../../assets/icons/up.svg';
import { ReactComponent as DownIcon } from '../../../assets/icons/down.svg';
import { ReactComponent as CrossIcon } from '../../../assets/icons/cross.svg';

// Components
import FlashMessage from '../../molecules/FlashMessage/FlashMessage';

import styles from './InputField.module.scss';

function InputField({
  id,
  name,
  label,
  value,
  full,
  noLeftBorder,
  className,
  error,
  isRequired,
  setFieldValue,
  textarea,
  disabled,
  maxWidth,
  step,
  max,
  disabledBrowserSuggestions,
  clearable,
  minNumberValue,
  allowNegativeValue,
  formik,
  ...props
}) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const [viewPassword, setViewPassword] = useState(false);

  const handleChangeValue = (type) => {
    if (!isNil(value) && setFieldValue) {
      setFieldValue(
        name,
        !isNil(max) && value >= max && type === 'up' ? max : type === 'up' ? (value + step) : (value - step),
      );
    }
  };

  useEffect(() => {
    if (props.type === 'number' && value <= 0 && !allowNegativeValue) setFieldValue(name, 0);
    if (props.type === 'number' && minNumberValue && value < minNumberValue) setFieldValue(name, minNumberValue);
  }, [value]);

  return (
    <div className={`${full ? 'w-100' : ''} ${className || ''}`}>
      <div
        className={`
          ${styles.input_wrapper}
          ${disabled ? styles.disabled : ''}
          ${textarea ? styles.textarea : ''}
          ${error ? styles.error : ''}
          d-flex a-center
          ${full ? 'w-100' : ''}
          ${noLeftBorder ? styles.no_left_border : ''}
          ${clearable ? styles.clearable : ''}
        `}
        style={maxWidth && !isMobile ? { maxWidth } : {}}
      >
        {textarea ? (
          <textarea
            id={id}
            name={name}
            placeholder={label}
            value={!isNil(value) ? value : ''}
            className={`${value ? styles.has_value : ''} ${props.type === 'number' ? styles.number : ''}`}
            disabled={disabled}
            {...props}
          />
        ) : (
          <>
            <input
              id={id}
              name={name}
              placeholder={label}
              value={!isNil(value) ? value : ''}
              className={`${value ? styles.has_value : ''} ${props.type === 'number' ? styles.number : ''}`}
              {...props}
              type={viewPassword ? 'text' : props.type}
              autoComplete={disabledBrowserSuggestions ? (props.type === 'password' ? 'new-password' : 'off') : ''}
              disabled={disabled}
              onChange={formik?.handleChange || props.onChange}
              onBlur={formik?.handleBlur || props.onBlur}
            />
            {clearable && value ? (
              <button type="button" className="d-flex center p-10 c-pointer" onClick={() => setFieldValue(name, '')}>
                <CrossIcon width={20} height={20} className="text-c-grey-600" />
              </button>
            ) : null}
          </>
        )}

        {label ? (
          <label htmlFor={id} className="pointer-none">
            {label}
            {isRequired ? <span>*</span> : ''}
          </label>
        ) : null}

        {props.type === 'number' ? (
          <div className={`${styles.number_icons} d-flex d-col center`}>
            <button
              type="button"
              className="d-flex center"
              onClick={() => handleChangeValue('up')}
            >
              <UpIcon />
            </button>
            <button
              type="button"
              className="d-flex center"
              onClick={() => handleChangeValue('down')}
            >
              <DownIcon />
            </button>
          </div>
        ) : null}

        {props.type === 'password' ? (
          <button
            type="button"
            className={`${styles.view_pass} d-flex center p-10 ${viewPassword ? styles.active : ''}`}
            onClick={() => setViewPassword(!viewPassword)}
          >
            <EyeIcon />
          </button>
        ) : null}
      </div>

      {error ? (
        <div className="m-t-10">
          <FlashMessage message={t(error)} color="error" />
        </div>
      ) : null}
    </div>
  );
}

InputField.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  className: PropTypes.string,
  full: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  type: PropTypes.string,
  error: PropTypes.string,
  noLeftBorder: PropTypes.bool,
  isRequired: PropTypes.bool,
  setFieldValue: PropTypes.func,
  textarea: PropTypes.bool,
  disabled: PropTypes.bool,
  maxWidth: PropTypes.number,
  step: PropTypes.number,
  max: PropTypes.number,
  disabledBrowserSuggestions: PropTypes.bool,
  clearable: PropTypes.bool,
  minNumberValue: PropTypes.number,
  allowNegativeValue: PropTypes.bool,
  formik: PropTypes.shape({ handleChange: PropTypes.func, handleBlur: PropTypes.func }),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
};

InputField.defaultProps = {
  id: null,
  name: null,
  label: null,
  value: '',
  type: 'text',
  error: null,
  className: null,
  full: false,
  noLeftBorder: false,
  isRequired: false,
  setFieldValue: () => {},
  textarea: false,
  disabled: false,
  maxWidth: null,
  step: 1,
  max: null,
  disabledBrowserSuggestions: false,
  clearable: false,
  minNumberValue: null,
  allowNegativeValue: false,
  formik: null,
  onChange: () => {},
  onBlur: () => {},
};

export default InputField;
